import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Fingerprint,
  UserCircle,
  Tag,
  Users,
  Shield,
  RefreshCw as SyncIcon,
  Settings,
  LogOut,
  Moon,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import { cn } from "../../../utils/function";
import { useTheme } from "../../../context/theme-context";
import useRequest from "../../hook/use-req";
import { UserResponse } from "../../types";
import SuiteSwitcher from "../suite-switcher";
import { getStoredOrgRole } from "../../../utils/org-role";
import { TraitIcon } from "../../../assets/icon";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path?: string;
  children?: NavItem[];
}

// Static for now — Convo Unify is a new, separate frontend-only area (per
// the "coming from frontend for now" ask), not yet wired to a distinct
// backend nav tree the way CRM's sidebar is. Overview/Profiles reuse the
// existing CDP Overview/Profile Explorer pages; "Unify Profiles" reuses the
// CDP Unify dashboard (moved here from crm-pages/cdp/unify); everything
// else is a placeholder until it's scoped.
const NAV_ITEMS: NavItem[] = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, path: "/unify/overview" },
  { id: "unify-profiles", label: "Unify", icon: Fingerprint, path: "/unify/dashboard" },
  { id: "profiles", label: "Profiles", icon: UserCircle, path: "/unify/profiles" },
  {
    id: "traits",
    label: "Traits",
    icon: TraitIcon,
    children: [
      { id: "traits-all", label: "All Traits", icon: TraitIcon, path: "/unify/traits/all" },
      { id: "traits-custom", label: "Custom", icon: Tag, path: "/unify/traits/custom" },
      { id: "traits-computed", label: "Computed", icon: Tag, path: "/unify/traits/computed" },
      { id: "traits-predictive", label: "Predictive", icon: Tag, path: "/unify/traits/predictive" },
    ],
  },
  { id: "audiences", label: "Audiences", icon: Users, path: "/unify/audiences" },
  { id: "consent-privacy", label: "Consent and privacy", icon: Shield, path: "/unify/consent-privacy" },
  { id: "activation-sync", label: "Activation & Sync", icon: SyncIcon, path: "/unify/activation-sync" },
  { id: "settings", label: "Settings", icon: Settings, path: "/unify/settings" },
];

const UnifySidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark: darkMode, toggleTheme } = useTheme();
  const [expanded, setExpanded] = useState<string[]>(["traits"]);
  const [user, setUser] = useState<UserResponse | null>(null);
  const role = user?.payload?.role || localStorage.getItem("role") || "";
  const orgRole = user?.payload?.org_role || getStoredOrgRole();

  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("role");
    navigate("/");
  };

  const isActive = (path?: string) => !!path && location.pathname === path;
  const isBranchActive = (item: NavItem): boolean => isActive(item.path) || (item.children?.some(isBranchActive) ?? false);

  const toggleExpanded = (id: string) => {
    if (!open) {
      onToggle();
      setTimeout(() => setExpanded((prev) => (prev.includes(id) ? prev : [...prev, id])), 300);
      return;
    }
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const renderItem = (item: NavItem, level = 0): React.ReactNode => {
    const Icon = item.icon;
    const hasChildren = !!item.children?.length;
    const isExpanded = expanded.includes(item.id);
    const active = isBranchActive(item);
    const iconSize = level === 0 ? "w-5 h-5" : "w-4 h-4";

    const row = (
      <div
        onClick={hasChildren ? () => toggleExpanded(item.id) : undefined}
        className={cn(
          `flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer select-none`,
          open ? "gap-3.5 justify-start" : "justify-center",
          active
            ? level === 0
              ? "text-gray-900 dark:text-white"
              : "text-blue-600 dark:text-blue-400"
            : "text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
        )}
      >
        <Icon className={cn(iconSize, "shrink-0", active ? "text-gray-800 dark:text-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500")} />
        {open &&
          (hasChildren ? (
            <div className="flex items-center justify-between flex-1">
              <span className="tracking-tight">{item.label}</span>
              <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isExpanded ? "rotate-180" : "")} />
            </div>
          ) : (
            <span className="tracking-tight">{item.label}</span>
          ))}
      </div>
    );

    return (
      <div key={item.id} className="block">
        {hasChildren ? row : <Link to={item.path || "#"} className="block">{row}</Link>}
        {hasChildren && open && isExpanded && (
          <div className="ml-2 mt-1 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
            {item.children!.map((child) => renderItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col h-full m-3 rounded-lg bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 transition-all duration-300 ${
        open ? "w-[257px]" : "w-[64px]"
      }`}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        {open ? (
          <div className="flex items-center gap-1.5">
            <img className="w-5 h-5" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <SuiteSwitcher activeSuite="unify" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />
            <button
              className="ml-auto flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title="Collapse"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 w-7">
            <img className="w-5 h-5" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <SuiteSwitcher activeSuite="unify" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />
            <button
              className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title="Expand"
            >
              <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
            </button>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">{NAV_ITEMS.map((item) => renderItem(item))}</nav>

      <div className="px-3 py-4 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
        <div
          className={cn(
            "flex items-center px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
            open ? "justify-between" : "justify-center"
          )}
        >
          <div className="flex items-center gap-3.5">
            <Moon className="w-[18px] h-[18px] text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" strokeWidth={1.75} />
            {open && <span className="text-sm font-semibold text-gray-700 dark:text-white tracking-tight">Dark Mode</span>}
          </div>
          {open && (
            <button
              onClick={toggleTheme}
              className={cn(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none shrink-0",
                darkMode ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
              )}
              aria-label="Toggle dark mode"
            >
              <span
                className={cn(
                  "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200",
                  darkMode ? "translate-x-[18px]" : "translate-x-1"
                )}
              />
            </button>
          )}
        </div>

        <button
          className="flex w-full items-center gap-3.5 px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          onClick={handleSignOut}
        >
          <LogOut className="w-[18px] h-[18px] text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0" strokeWidth={1.75} />
          {open && <span className="tracking-tight">Log Out</span>}
        </button>
      </div>
    </div>
  );
};

export default UnifySidebar;
